import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Bookmark, LogOut, LayoutDashboard } from "lucide-react";
import { useAuth } from "@/lib/auth";
import type { AuthUser } from "@/lib/api";

export const Avatar = ({ user, className = "h-9 w-9" }: { user: AuthUser; className?: string }) =>
  user.picture ? (
    <img src={user.picture} alt={user.name} referrerPolicy="no-referrer" className={`${className} rounded-full border border-gold/60 object-cover`} />
  ) : (
    <span className={`${className} flex items-center justify-center rounded-full border border-gold/60 bg-obsidian-soft font-serif text-sm text-gold`}>
      {(user.name || user.email).slice(0, 1).toUpperCase()}
    </span>
  );

export default function UserMenu({ user }: { user: AuthUser }) {
  const [open, setOpen] = useState(false);
  const { logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button onClick={() => setOpen((o) => !o)} data-testid="nav-user-menu-trigger" aria-label="Account menu" className="flex items-center">
        <Avatar user={user} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 top-full w-60 border border-white/10 bg-obsidian-soft/95 p-2 backdrop-blur-xl"
            data-testid="nav-user-menu"
          >
            <p className="truncate px-4 pb-2 pt-2 text-xs text-zinc-500">{user.email}</p>
            <Link to="/account" data-testid="nav-user-menu-account" className="flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-300 hover:bg-white/5 hover:text-gold">
              <Bookmark className="h-4 w-4" /> My account
            </Link>
            {user.role === "admin" && (
              <Link to="/admin" data-testid="nav-user-menu-admin" className="flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-300 hover:bg-white/5 hover:text-gold">
                <LayoutDashboard className="h-4 w-4" /> Studio dashboard
              </Link>
            )}
            <button onClick={onLogout} data-testid="nav-user-menu-logout" className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-zinc-300 hover:bg-white/5 hover:text-red-400">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
